import { DeleteOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { useDebounce } from '@react-hook/debounce';
import {
    Button,
    Divider,
    Dropdown,
    Input,
    Modal,
    notification,
    Select,
    Switch,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnType, Key, SortOrder } from 'antd/lib/table/interface';
import { GenericOverviewTable } from 'components/GenericOverviewTable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { deleteDestination, getAllDestinations } from 'api/destinations.api';
import { DestinationContentSources } from 'constants/destination-content-sources';
import {
    DestinationsListState,
    useDestinationsListState,
} from 'hooks/use-destinations-list-state';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import { DestinationListImage } from './DestinationListImage';
import styles from './style.module.css';
import { DestinationStatuses } from 'constants/destination-statuses';
import { DestinationGeoType } from 'constants/destination-geo-type';
import { formatCompactNumber } from 'utils/format-number';

const { Search } = Input;

export function DestinationsOverview() {
    const navigate = useNavigate();
    const {
        query,
        page,
        sortColumn,
        sortOrder,
        onlyWithoutPhotos,
        hasGuide,
        source,
        status,
        geoType,
        returnToUrl,
        updateState,
    } = useDestinationsListState();
    const [debouncedQuery, setDebouncedQuery] = useDebounce(query, 300);

    const [isDeleting, setIsDeleting] = useState(false);

    const addMenuItems: MenuProps['items'] = [
        {
            key: 'place',
            label: 'Place',
            onClick: () =>
                navigate(
                    `/destinations/add?geo_type=${DestinationGeoType.PLACE}`,
                ),
        },
        {
            key: 'country',
            label: 'Country',
            onClick: () =>
                navigate(
                    `/destinations/add?geo_type=${DestinationGeoType.COUNTRY}`,
                ),
        },
    ];
    const filterWithEnoughCharacters =
        debouncedQuery.length > 2 ? debouncedQuery : '';

    // Memoize the API parameters to prevent unnecessary re-renders
    const apiParams = useMemo(
        () => ({
            query: filterWithEnoughCharacters,
            onlyWithoutPhotos,
            hasGuide,
            status,
            source,
            geo_type: geoType,
            sortColumn,
            sortOrder,
        }),
        [
            filterWithEnoughCharacters,
            onlyWithoutPhotos,
            hasGuide,
            source,
            status,
            geoType,
            sortColumn,
            sortOrder,
        ],
    );

    const apiGetter = useCallback(
        (pageNumber: number) => {
            if (pageNumber !== page) {
                updateState({ page: pageNumber });
            }

            return getAllDestinations({
                ...apiParams,
                page: pageNumber,
            });
        },
        [apiParams, page, updateState],
    );

    const handleDeleteDestination = useCallback(
        async (destinationId: number) => {
            setIsDeleting(true);
            try {
                await deleteDestination(destinationId);
                await apiGetter(page);
            } catch (error) {
                notification.error({
                    message: 'Error deleting destination',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                });
            } finally {
                setIsDeleting(false);
            }
        },
        [apiGetter, page],
    );

    const handleShowDeleteConfirmation = useCallback(
        (destination: DestinationSchema) => {
            Modal.confirm({
                type: 'warning',
                title: 'Delete destination?',
                content: 'Are you sure you want to delete this destination?',
                onOk: () => {
                    handleDeleteDestination(destination.id);
                },
                okText: 'Delete',
                okButtonProps: {
                    danger: true,
                },
                cancelText: 'Cancel',
            });
        },
        [handleDeleteDestination],
    );

    const columns = useMemo<ColumnType<DestinationSchema>[]>(
        () => [
            {
                title: 'Image',
                dataIndex: 'image_url',
                key: 'cms_media_id',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <DestinationListImage
                        destination={destination}
                        onUpdate={() => apiGetter(page)} // Triggering the update of the table
                    />
                ),
                sorter: false,
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'locality',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Link
                        to={`/destinations/${destination.id}?tab=basicInfo`}
                        state={{
                            returnTo: returnToUrl,
                        }}
                    >
                        {destination.name || destination.locality}
                    </Link>
                ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'locality',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Country',
                dataIndex: 'country',
                key: 'country',
                render: (_text: unknown, destination: DestinationSchema) =>
                    destination.geo_type === DestinationGeoType.COUNTRY ? (
                        <span className={styles.emptyCell}> - </span>
                    ) : (
                        <Link
                            to={`/destinations/${destination.id}?tab=basicInfo`}
                            state={{
                                returnTo: returnToUrl,
                            }}
                        >
                            {destination.country}
                        </Link>
                    ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'country',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Source',
                dataIndex: 'content_source',
                key: 'content_source',
                render: (_text: unknown, destination: DestinationSchema) =>
                    getContentSource(destination),
                sorter: false,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (_text: unknown, destination: DestinationSchema) =>
                    destination.status === DestinationStatuses.PUBLISHED ? (
                        <Tag color="green">Published</Tag>
                    ) : (
                        <Tag color="blue">Draft</Tag>
                    ),
                sorter: false,
            },
            {
                title: 'Geo type',
                dataIndex: 'geo_type',
                key: 'geo_type',
                render: (_text: unknown, destination: DestinationSchema) =>
                    destination.geo_type === DestinationGeoType.COUNTRY ? (
                        <Tag color="green">Country</Tag>
                    ) : (
                        <Tag color="blue">Place</Tag>
                    ),
                sorter: false,
            },
            {
                title: (
                    <div className={styles.visitorColumnHeader}>
                        <span>Visitors</span>
                        <span className={styles.visitorColumnHeader__period}>
                            (last year)
                        </span>
                    </div>
                ),
                dataIndex: 'visitors_t1',
                key: 'visitors_t1',
                align: 'center',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Tooltip
                        title={
                            <div className={styles.visitorTooltip}>
                                <div>
                                    <span
                                        className={styles.visitorTooltip__label}
                                    >
                                        Last year:
                                    </span>
                                    <span>
                                        {destination.visitors_t1?.toLocaleString() ??
                                            'No data'}
                                    </span>
                                </div>
                                <div>
                                    <span
                                        className={styles.visitorTooltip__label}
                                    >
                                        Last month:
                                    </span>
                                    <span>
                                        {destination.visitors_t2?.toLocaleString() ??
                                            'No data'}
                                    </span>
                                </div>
                            </div>
                        }
                    >
                        <span className={styles.visitorCount}>
                            {formatCompactNumber(destination.visitors_t1)}
                        </span>
                    </Tooltip>
                ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'visitors_t1',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Actions',
                dataIndex: 'actions',
                key: 'actions',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Button
                        size="middle"
                        icon={<DeleteOutlined />}
                        onClick={() =>
                            handleShowDeleteConfirmation(destination)
                        }
                        loading={isDeleting}
                        disabled={
                            destination.geo_type === DestinationGeoType.COUNTRY
                        }
                        danger
                    />
                ),
                sorter: false,
            },
        ],
        [
            apiGetter,
            handleShowDeleteConfirmation,
            isDeleting,
            page,
            returnToUrl,
            sortColumn,
            sortOrder,
        ],
    );

    const handleSort = useCallback(
        (column: Key | undefined, order: SortOrder | undefined) => {
            updateState({
                sortColumn: column ? column.toString() : undefined,
                sortOrder: order ? getSortOrder(order) : undefined,
            });
        },
        [updateState],
    );

    useEffect(() => {
        updateState({ query: debouncedQuery });
    }, [debouncedQuery, updateState]);

    /**
     * Add button is different in development and production.
     * In development, we use a dropdown to select the geo type.
     * In production, we use a link to the add destination page.
     * This is because in development, we want to be able to add destinations to both localities and countries.
     * In production, we want to be able to add destinations to localities only because all the countries are already in the database.
     */
    const addButton = useMemo(
        () =>
            process.env.NODE_ENV === 'development' ? (
                <Dropdown menu={{ items: addMenuItems }} trigger={['click']}>
                    <Button type="primary" size="large" icon={<PlusOutlined />}>
                        Add <DownOutlined />
                    </Button>
                </Dropdown>
            ) : (
                <Link
                    to={`/destinations/add?geo_type=${DestinationGeoType.PLACE}`}
                    className="full-page-header-tools-add-button"
                >
                    <Button type="primary" size="large" icon={<PlusOutlined />}>
                        Add
                    </Button>
                </Link>
            ),
        [addMenuItems],
    );

    return (
        <div className="full-page">
            <PageHeader ghost={false} title="Destinations" extra={addButton}>
                <DestinationFilters
                    query={query}
                    source={source}
                    status={status}
                    geoType={geoType}
                    onlyWithoutPhotos={onlyWithoutPhotos}
                    hasGuide={hasGuide}
                    setDebouncedQuery={setDebouncedQuery}
                    updateState={updateState}
                />
            </PageHeader>
            <div className="full-page-content">
                <div className="max-content-width mb-m">
                    <GenericOverviewTable
                        className={styles.table}
                        columns={columns}
                        apiGetter={apiGetter}
                        emptyTableMessage={
                            query
                                ? 'No destinations found with this text.'
                                : 'Sorry, no destinations yet.'
                        }
                        sortCallback={handleSort}
                        initialCurrentPage={page}
                    ></GenericOverviewTable>
                </div>
            </div>
        </div>
    );
}

type DestinationFiltersProps = Readonly<{
    query: string;
    source?: number | null;
    status?: number | null;
    geoType?: number | null;
    onlyWithoutPhotos: boolean;
    hasGuide: boolean;
    setDebouncedQuery: (query: string) => void;
    updateState: (state: Partial<DestinationsListState>) => void;
}>;

function DestinationFilters({
    query,
    source = null,
    status = null,
    geoType = null,
    onlyWithoutPhotos,
    hasGuide,
    setDebouncedQuery,
    updateState,
}: DestinationFiltersProps) {
    return (
        <div className={styles.filters}>
            <div className={styles.filter__row}>
                <Search
                    className="full-page-header-tools-filter"
                    placeholder="Search for destinations"
                    size="large"
                    defaultValue={query}
                    onChange={(e) => setDebouncedQuery(e.target.value)}
                />
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">Source:</Typography.Text>
                    <Select
                        size="large"
                        defaultValue={null}
                        value={source}
                        onChange={(value) => updateState({ source: value })}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationContentSources.MANUAL}>
                            Manual
                        </Select.Option>
                        <Select.Option value={DestinationContentSources.AI}>
                            AI
                        </Select.Option>
                        <Select.Option
                            value={DestinationContentSources.FROM_GUIDES}
                        >
                            Guides
                        </Select.Option>
                    </Select>
                </div>
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">Status:</Typography.Text>
                    <Select
                        size="large"
                        defaultValue={null}
                        value={status}
                        onChange={(value) => updateState({ status: value })}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationStatuses.DRAFT}>
                            Draft
                        </Select.Option>
                        <Select.Option value={DestinationStatuses.PUBLISHED}>
                            Published
                        </Select.Option>
                    </Select>
                </div>
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Geo type:
                    </Typography.Text>
                    <Select
                        size="large"
                        defaultValue={null}
                        value={geoType}
                        onChange={(value) => updateState({ geoType: value })}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationGeoType.PLACE}>
                            Place
                        </Select.Option>
                        <Select.Option value={DestinationGeoType.COUNTRY}>
                            Country
                        </Select.Option>
                    </Select>
                </div>
            </div>
            <div className={styles.filter__row}>
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Missing cover image:
                    </Typography.Text>
                    <Switch
                        checked={onlyWithoutPhotos}
                        onChange={(checked) =>
                            updateState({ onlyWithoutPhotos: checked })
                        }
                    />
                </div>
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Has guide:
                    </Typography.Text>
                    <Switch
                        checked={hasGuide}
                        onChange={(checked) =>
                            updateState({ hasGuide: checked })
                        }
                    />
                </div>
            </div>
        </div>
    );
}

function getSortOrder(order: SortOrder | undefined) {
    return order === 'ascend' ? 'asc' : 'desc';
}

type GetGenericTableSortOrderParams = {
    column: string;
    currentSortColumn?: string;
    sortOrder?: string;
};

function getGenericTableSortOrder({
    column,
    currentSortColumn,
    sortOrder,
}: GetGenericTableSortOrderParams) {
    if (currentSortColumn === column) {
        if (!sortOrder) {
            return undefined;
        }

        return sortOrder === 'asc' ? 'ascend' : 'descend';
    }
    return undefined;
}

function getContentSource(destination: DestinationSchema): JSX.Element {
    if (
        !destination.contents ||
        destination.contents.every(
            (content) =>
                (!content.title || content.title.trim() === '') &&
                (!content.body || content.body.trim() === ''),
        )
    ) {
        return <Tag color="gray">No content</Tag>;
    }

    const sources = destination.contents.map((content) => content.source);
    const uniqueSources = [...new Set(sources)];

    const allManualOrGuides = uniqueSources.every(
        (source) =>
            source === DestinationContentSources.MANUAL ||
            source === DestinationContentSources.FROM_GUIDES,
    );

    if (allManualOrGuides) {
        return <Tag color="orange">Manual</Tag>;
    }

    if (
        uniqueSources.length === 1 &&
        uniqueSources[0] === DestinationContentSources.AI
    ) {
        return <Tag color="green">AI</Tag>;
    }

    return <Tag color="purple">Mixed</Tag>;
}
